import { useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { updateMyProfile, useTeamMembers } from '../tareas/api';
import { PROFILE_PALETTE } from './palette';

// Edición del perfil propio: nombre y color (el color es el que te identifica
// en avatares, nombres de hilos y menciones).
export default function ProfileSettings() {
  const { profile, reloadProfile } = useAuth();
  const { members } = useTeamMembers();
  const [nombre, setNombre] = useState(profile?.nombre ?? '');
  const [color, setColor] = useState(profile?.color ?? '#2563eb');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const dirty = nombre.trim() !== (profile?.nombre ?? '') || color !== (profile?.color ?? '');

  // Colores ya en uso por OTROS compañeros: se muestran bloqueados para que dos
  // personas del mismo equipo no compartan color.
  const takenColors = new Set(
    members.filter((m) => m.id !== profile?.id).map((m) => m.color),
  );
  // Si el color actual no está en la paleta (perfil antiguo), lo mostramos
  // delante para que el usuario vea su selección actual hasta que elija otro.
  const swatches = PROFILE_PALETTE.includes(color) ? PROFILE_PALETTE : [color, ...PROFILE_PALETTE];

  const save = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!profile?.id || !nombre.trim()) { setError('El nombre no puede estar vacío.'); return; }
    setBusy(true);
    setError('');
    try {
      await updateMyProfile(profile.id, { nombre, color });
      reloadProfile();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={styles.card}>
      <h2 style={styles.cardTitle}>Tu perfil</h2>
      <form onSubmit={save} style={styles.form}>
        <span style={{ ...styles.avatar, background: color }}>
          {(nombre || '?').trim().charAt(0).toUpperCase()}
        </span>
        <div style={styles.fields}>
          <div style={styles.row}>
            <label style={styles.label}>Nombre</label>
            <input
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              maxLength={60}
              placeholder="Tu nombre"
              style={styles.input}
            />
          </div>
          <div style={{ ...styles.row, alignItems: 'flex-start' }}>
            <label style={{ ...styles.label, paddingTop: 4 }}>Color</label>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={styles.swatches}>
                {swatches.map((c) => {
                  const selected = c === color;
                  const taken = !selected && takenColors.has(c);
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => { if (!taken) setColor(c); }}
                      disabled={taken}
                      title={taken ? 'En uso por un compañero' : c}
                      style={{
                        ...styles.swatch,
                        background: c,
                        outline: selected ? '2px solid #0f172a' : '2px solid transparent',
                        opacity: taken ? 0.25 : 1,
                        cursor: taken ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {selected ? '✓' : taken ? '×' : ''}
                    </button>
                  );
                })}
              </div>
              <span style={{ ...styles.hint, display: 'block', marginTop: 8 }}>
                Te identifica en avatares, nombres e hilos. Los colores que ya usan tus compañeros aparecen bloqueados.
              </span>
            </div>
          </div>
          {error && <p style={styles.error}>{error}</p>}
        </div>
        <button type="submit" disabled={busy || !dirty} style={{ ...styles.btn, opacity: dirty ? 1 : 0.5 }}>
          {busy ? '...' : saved ? '¡Guardado!' : 'Guardar'}
        </button>
      </form>
    </section>
  );
}

const styles: Record<string, CSSProperties> = {
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 22, marginBottom: 18 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 16px' },
  form: { display: 'flex', alignItems: 'flex-start', gap: 16 },
  avatar: {
    width: 52, height: 52, minWidth: 52, borderRadius: '50%', color: '#fff',
    fontWeight: 700, fontSize: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  fields: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 },
  row: { display: 'flex', alignItems: 'center', gap: 10 },
  label: { width: 64, fontSize: 13, fontWeight: 600, color: '#64748b' },
  input: { flex: 1, border: '1px solid #cbd5e1', borderRadius: 8, padding: '9px 12px', fontSize: 14, outline: 'none', background: '#fff' },
  swatches: { display: 'flex', flexWrap: 'wrap', gap: 8, maxWidth: 340 },
  swatch: {
    width: 26, height: 26, borderRadius: '50%', border: 'none', padding: 0,
    color: '#fff', fontSize: 13, fontWeight: 700, lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    outlineOffset: 2,
  },
  hint: { fontSize: 12.5, color: '#94a3b8' },
  error: { color: '#dc2626', fontSize: 13, margin: 0 },
  btn: { background: '#0f172a', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' },
};
